import type { DeepKeys, DeepValue, Updater } from "@tanstack/react-form";
import type { z } from "zod";

import type { CheckboxCustomProps } from "@/components/checkbox-custom";
import CheckboxCustom from "@/components/checkbox-custom";
import LabeledField from "@/components/form/labeled-field";
import { cn } from "@/lib/utils";
import type { FieldBaseProps } from "@/types/form";
import { getOnChangeFromZod } from "@/utils/form";

interface MultipleCheckboxOption extends Partial<CheckboxCustomProps> {
  value: string | number;
  label: string;
}

export type FieldMultipleCheckboxProps<
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
> = FieldBaseProps<TFormData, TName, TSchema, TData> & {
  options: Array<MultipleCheckboxOption>;
  onOptionCheckedChange?: (
    option: MultipleCheckboxOption,
    checked: boolean
  ) => void;
  orientation?: "horizontal" | "vertical";
  disabled?: boolean;
};

const FieldMultipleCheckbox = <
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
>({
  Field,
  name,
  schema,
  label,
  labelProps,
  options,
  orientation = "vertical",
  gridRow,
  onOptionCheckedChange,
  ...props
}: FieldMultipleCheckboxProps<TFormData, TName, TSchema, TData>) => {
  return (
    <Field
      name={name}
      validators={{
        onChange: schema
          ? getOnChangeFromZod(schema, name as string)
          : undefined,
      }}
      children={(field) => {
        const fieldValue = field.state.value as Array<string | number>;
        return (
          <LabeledField
            label={label}
            fieldName={field.name.toString()}
            errors={field.state.meta.errors}
            gridRow={gridRow}
            {...labelProps}
          >
            <div
              className={cn([
                orientation === "vertical"
                  ? "grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-[512px]"
                  : "flex flex-col sm:flex-row align-center gap-x-6 gap-y-4",
              ])}
            >
              {options.map((option) => {
                const { value: optionValue, ...optionProps } = option;
                return (
                  <CheckboxCustom
                    key={`${name}_${optionValue}`}
                    id={`${name}_${optionValue}`}
                    checked={fieldValue?.includes(optionValue) ?? false}
                    onCheckedChange={(value) => {
                      if (onOptionCheckedChange)
                        onOptionCheckedChange(option, !!value);
                      const newArray = field.state.value ? [...fieldValue] : [];
                      if (value) newArray.push(optionValue);
                      else newArray.splice(newArray.indexOf(optionValue), 1);
                      (
                        field.handleChange as (
                          updater: Updater<Array<string | number> | null>
                        ) => void
                      )(newArray.length > 0 ? newArray : null);
                    }}
                    {...props}
                    {...optionProps}
                  />
                );
              })}
            </div>
          </LabeledField>
        );
      }}
    />
  );
};

export default FieldMultipleCheckbox;
